import { TokenService } from './tokenService';

interface ValidationResult {
  isValid: boolean;
  message: string;
  shouldLogout?: boolean;
}

interface DomainValidationOptions {
  allowedDomains?: string[];
  requireDomainMatch?: boolean;
}

/**
 * Service for handling authentication validation with improved security
 */
export class AuthValidationService {
  /**
   * Validate token and determine if user should be logged out
   * @param token - JWT token to validate
   * @param safetyMarginSeconds - Safety margin for token expiration
   * @returns Validation result with action recommendations
   */
  static validateTokenForLogout(token: string | null | undefined, safetyMarginSeconds: number = 0): ValidationResult {
    if (!token) {
      return {
        isValid: false,
        message: 'No token available',
        shouldLogout: true
      };
    }

    const timeUntilExpiration = TokenService.getTimeUntilExpiration(token);
    
    if (timeUntilExpiration === null) {
      return {
        isValid: false,
        message: 'Invalid or malformed token',
        shouldLogout: true
      };
    }

    const safetyMargin = safetyMarginSeconds * 1000;
    
    if (timeUntilExpiration <= safetyMargin) {
      const secondsExpired = Math.abs(Math.round(timeUntilExpiration / 1000));
      return {
        isValid: false,
        message: timeUntilExpiration <= 0 
          ? `Token expired ${secondsExpired} seconds ago`
          : `Token expires in ${secondsExpired} seconds (within safety margin)`,
        shouldLogout: true
      };
    }

    return {
      isValid: true,
      message: `Token valid for ${Math.round(timeUntilExpiration / 1000)} seconds`
    };
  }

  /**
   * Validate domain access based on token claims
   * @param token - JWT token
   * @param options - Domain validation options
   * @returns Validation result
   */
  static validateDomainAccess(token: string | null | undefined, options: DomainValidationOptions = {}): ValidationResult {
    if (!token) {
      return {
        isValid: false,
        message: 'No token available for domain validation'
      };
    }

    // For now, return valid as domain validation is not fully implemented
    // This is a placeholder for future domain validation logic
    const currentDomain = window.location.hostname;
    
    if (options.allowedDomains && options.allowedDomains.length > 0) {
      const isDomainAllowed = options.allowedDomains.some(domain => 
        currentDomain === domain || currentDomain.endsWith(`.${domain}`)
      );
      
      if (!isDomainAllowed) {
        return {
          isValid: false,
          message: `Domain ${currentDomain} is not in allowed domains list`,
          shouldLogout: true
        };
      }
    }

    return {
      isValid: true,
      message: `Domain access validated for ${currentDomain}`
    };
  }

  /**
   * Validate user roles against required roles
   * @param userRoles - User's roles from token
   * @param requiredRoles - Required roles for access
   * @param requireAll - Whether all roles are required (default: false - any role is sufficient)
   * @returns Validation result
   */
  static validateUserRoles(userRoles: string[], requiredRoles: string[], requireAll: boolean = false): ValidationResult {
    if (!requiredRoles || requiredRoles.length === 0) {
      return {
        isValid: true,
        message: 'No role requirements specified'
      };
    }

    if (!userRoles || userRoles.length === 0) {
      return {
        isValid: false,
        message: 'User has no roles assigned'
      };
    }

    const hasRequiredRoles = requireAll
      ? requiredRoles.every(role => userRoles.includes(role))
      : requiredRoles.some(role => userRoles.includes(role));

    if (!hasRequiredRoles) {
      const requirement = requireAll ? 'all' : 'any';
      return {
        isValid: false,
        message: `User lacks required roles. Needs ${requirement} of: ${requiredRoles.join(', ')}`
      };
    }

    return {
      isValid: true,
      message: `User has required roles: ${userRoles.filter(role => requiredRoles.includes(role)).join(', ')}`
    };
  }

  /**
   * Comprehensive authentication validation
   * @param token - JWT token
   * @param options - Validation options
   * @returns Comprehensive validation result
   */
  static validateAuthentication(
    token: string | null | undefined,
    options: {
      safetyMarginSeconds?: number;
      domainOptions?: DomainValidationOptions;
      requiredRoles?: string[];
      requireAllRoles?: boolean;
    } = {}
  ): ValidationResult {
    // First validate token
    const tokenValidation = this.validateTokenForLogout(token, options.safetyMarginSeconds);
    if (!tokenValidation.isValid) {
      return tokenValidation;
    }

    // Validate domain access if options provided
    if (options.domainOptions) {
      const domainValidation = this.validateDomainAccess(token, options.domainOptions);
      if (!domainValidation.isValid) {
        return domainValidation;
      }
    }

    // Validate roles if required
    if (options.requiredRoles && options.requiredRoles.length > 0) {
      const userInfo = TokenService.getUserInfo(token);
      const roleValidation = this.validateUserRoles(
        userInfo.roles,
        options.requiredRoles,
        options.requireAllRoles
      );
      if (!roleValidation.isValid) {
        return roleValidation;
      }
    }

    return {
      isValid: true,
      message: 'Authentication validation passed'
    };
  }

  /**
   * Check if error indicates token refresh is needed
   * @param error - Error object or string
   * @returns boolean indicating if refresh is needed
   */
  static isTokenRefreshError(error: any): boolean {
    if (!error) return false;

    const errorString = error.toString().toLowerCase();
    const refreshErrorPatterns = [
      'token expired',
      'token invalid',
      'unauthorized',
      '401',
      'invalid_token',
      'token_expired'
    ];

    return refreshErrorPatterns.some(pattern => errorString.includes(pattern));
  }

  /**
   * Check if error indicates logout is required
   * @param error - Error object or string
   * @returns boolean indicating if logout is required
   */
  static isLogoutRequiredError(error: any): boolean {
    if (!error) return false;

    const errorString = error.toString().toLowerCase();
    const logoutErrorPatterns = [
      'invalid_grant',
      'refresh token expired',
      'refresh token invalid',
      'failed to refresh token',
      'token refresh failed'
    ];

    return logoutErrorPatterns.some(pattern => errorString.includes(pattern));
  }

  /**
   * Get current subdomain for domain validation
   * @returns Current subdomain or empty string
   */
  static getCurrentSubdomain(): string {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    
    // If hostname has more than 2 parts, first part is likely subdomain
    if (parts.length > 2) {
      return parts[0];
    }
    
    return '';
  }

  /**
   * Log validation result for debugging
   * @param result - Validation result
   * @param context - Context for logging
   */
  static logValidationResult(result: ValidationResult, context: string = 'Validation'): void {
    const level = result.isValid ? 'log' : 'warn';
    const action = result.shouldLogout ? ' (logout required)' : '';
    console[level](`${context}: ${result.message}${action}`);
  }
}